<template>
  <header class="app-header">
    <div class="header-left hamburger-container" ref="menuContainer">
      <!-- Hamburger for Terms -->
      <button
        type="button"
        class="hamburger"
        :class="{ 'menu-open': menuOpen }"
        @click="toggleMenu"
        aria-label="Toggle navigation menu"
        :aria-expanded="menuOpen"
        aria-controls="header-nav-menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
      <nav
        id="header-nav-menu"
        class="nav-menu"
        :class="{ 'menu-open': menuOpen }"
        aria-label="Header navigation menu"
      >
        <transition name="fade">
          <div v-if="menuOpen" class="nav-content">
            <!-- Auth Info -->
            <div v-if="authStore.isAuthenticated" class="user-info">
              <div class="user-avatar">
                {{ getUserInitial() }}
              </div>
              <div class="user-details">
                <span class="user-name">{{ authStore.userDetails.name || 'User' }}</span>
                <span class="user-email">{{ authStore.userDetails.email }}</span>
              </div>
            </div>

            <div class="nav-divider" v-if="authStore.isAuthenticated"></div>

            <template v-if="authStore.isAuthenticated">
              <router-link to="/profile" @click="handleNavItemClick">Account &amp; Switch</router-link>
              <router-link to="/dashboard" @click="handleNavItemClick">Dashboard</router-link>
              <router-link to="/dashboard/experiences" @click="handleNavItemClick">Experience Library</router-link>
              <router-link to="/billing" @click="handleNavItemClick">Billing</router-link>
              <div class="nav-divider"></div>
            </template>

            <template v-if="currentNavigation.isExternal">
              <a
                :href="currentNavigation.path"
                target="_blank"
                rel="noopener noreferrer"
                @click="handleNavItemClick"
              >
                {{ currentNavigation.text }}
              </a>
            </template>
            <template v-else>
              <router-link :to="currentNavigation.path" @click="handleNavItemClick">
                {{ currentNavigation.text }}
              </router-link>
            </template>

            <!-- Additional Links -->
            <a
              v-if="!currentNavigation.isExternal && currentNavigation.text !== 'Home'"
              href="/"
              @click="handleNavItemClick"
            >
              Home
            </a>

          </div>
        </transition>
      </nav>
    </div>

    <div class="header-right" ref="authContainer">
      <button
        v-if="!authStore.isAuthenticated"
        class="auth-btn"
        @click="handleAuthIconClick"
        aria-label="Log in"
      >
        <svg
          class="auth-icon-svg"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle cx="12" cy="7.5" r="3.25" />
          <path d="M5.5 19.25c.9-3.05 3.52-4.75 6.5-4.75s5.6 1.7 6.5 4.75" />
        </svg>
      </button>

      <button
        v-else
        class="avatar-btn"
        @click="handleAuthIconClick"
        aria-label="Account menu"
        :aria-expanded="authMenuOpen"
      >
        <span class="avatar-initials">{{ getUserInitial() }}</span>
      </button>

      <transition name="fade">
        <div
          v-if="authStore.isAuthenticated && authMenuOpen"
          class="auth-menu account-menu"
          role="menu"
          aria-label="Account menu"
        >
          <div class="account-menu-header">
            <div class="account-menu-avatar">{{ getUserInitial() }}</div>
            <div class="account-menu-identity">
              <span class="account-menu-name">{{ authStore.userDetails.name || 'Account' }}</span>
              <span class="account-menu-email">{{ authStore.userDetails.email }}</span>
            </div>
          </div>

          <div class="account-menu-context" v-if="authStore.activeOrgId || authStore.activeTenantId">
            <small class="account-menu-context-label">Active</small>
            <span class="account-menu-context-row" v-if="authStore.activeOrgId">
              <strong>Org</strong>
              <span class="account-menu-context-value">{{ authStore.activeOrgName || authStore.activeOrgId }}</span>
            </span>
            <span class="account-menu-context-row" v-if="authStore.activeTenantId">
              <strong>Tenant</strong>
              <span class="account-menu-context-value">{{ authStore.activeTenantName || authStore.activeTenantId }}</span>
            </span>
          </div>

          <div class="account-menu-divider"></div>

          <button class="logout-btn auth-logout-btn" @click="handleLogout">
            Sign Out
          </button>
        </div>
      </transition>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth.store'

export default defineComponent({
  emits: ['open-auth', 'show-how-it-works'],
  props: {
    currentSection: {
      type: String,
      default: 'splash'
    }
  },
  setup(_, { emit }) {
    const menuOpen = ref(false)
    const menuContainer = ref<HTMLElement | null>(null)
    const authMenuOpen = ref(false)
    const authContainer = ref<HTMLElement | null>(null)
    const route = useRoute()
    const authStore = useAuthStore()
    const preservedScrollY = ref(0)

    const captureScrollPosition = () => {
      preservedScrollY.value = window.scrollY || document.documentElement.scrollTop || 0
    }

    const restoreScrollPosition = () => {
      const targetY = preservedScrollY.value
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: targetY, left: 0, behavior: 'auto' })
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: targetY, left: 0, behavior: 'auto' })
        })
      })
    }

    const closeMenuWithoutScrollShift = () => {
      if (!menuOpen.value) {
        return
      }
      captureScrollPosition()
      menuOpen.value = false
      nextTick(restoreScrollPosition)
    }

    const currentNavigation = computed(() => {
      const path = route.path

      if (path === '/') {
        return {
          path: '/terms-and-conditions',
          text: 'Terms and Conditions',
          isExternal: false,
        }
      } else if (path === '/terms' || path === '/terms-and-conditions') {
        return {
          path: '/',
          text: 'Back to Forms',
          isExternal: false,
        }
      } else if (path === '/entry' || path === '/holding') {
        return {
          path: 'https://innereality.com',
          text: 'Visit Innereality.com',
          isExternal: true,
        }
      }

      // Default fallback
      return {
        path: '/',
        text: 'Home',
        isExternal: false,
      }
    })

    const toggleMenu = () => {
      captureScrollPosition()
      menuOpen.value = !menuOpen.value
      authMenuOpen.value = false
      nextTick(restoreScrollPosition)
    }

    const handleNavItemClick = () => {
      closeMenuWithoutScrollShift()
      authMenuOpen.value = false
    }

    const handleAuthIconClick = () => {
      if (!authStore.isAuthenticated) {
        emit('open-auth')
        return
      }

      captureScrollPosition()
      authMenuOpen.value = !authMenuOpen.value
      menuOpen.value = false
      nextTick(restoreScrollPosition)
    }

    const getUserInitial = () => {
      const name = authStore.userDetails.name || authStore.userDetails.email || 'U';
      return name.charAt(0).toUpperCase();
    }

    const handleLogout = async () => {
      await authStore.logout();
      menuOpen.value = false;
      authMenuOpen.value = false;
      nextTick(restoreScrollPosition)
    }

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (!menuOpen.value && !authMenuOpen.value) {
        return
      }

      const target = event.target as HTMLElement | null
      if (!target) {
        return
      }

      if (menuOpen.value && menuContainer.value && !menuContainer.value.contains(target)) {
        closeMenuWithoutScrollShift()
      }

      if (authMenuOpen.value && authContainer.value && !authContainer.value.contains(target)) {
        authMenuOpen.value = false
      }
    }

    onMounted(() => {
      authStore.checkAuth()

      document.addEventListener('click', handleOutsideClick)
      document.addEventListener('touchstart', handleOutsideClick)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    })

    watch(
      () => route.path,
      () => {
        captureScrollPosition()
        menuOpen.value = false
        authMenuOpen.value = false
        nextTick(restoreScrollPosition)
      }
    )

    return {
      menuOpen,
      authMenuOpen,
      toggleMenu,
      handleNavItemClick,
      handleAuthIconClick,
      currentNavigation,
      authStore,
      getUserInitial,
      handleLogout,
      menuContainer,
      authContainer,
    }
  },
})
</script>

<style scoped>
.hamburger-container {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}

.app-header {
  position: fixed;
  top: var(--header-top-offset);
  left: 0;
  width: 100%;
  height: calc(var(--header-height) + var(--header-shell-extra-height));
  background: var(--vt-c-background-primary);
  border-bottom: 1px solid rgba(var(--vt-c-accent-rgb), 0.22);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--header-inline-padding);
  z-index: calc(var(--base-layout-header-z-index) + 1);
  overflow: visible;
  transition: background-color 0.2s ease, border-bottom-color 0.2s ease;
}

@media (max-width: 1023px) {
  .app-header {
    top: 0;
    background: #23384d;
    border-bottom-color: rgba(150, 250, 201, 0.18);
    z-index: calc(var(--base-layout-modal-z-index) + 120);
  }
}

.header-left {
  display: flex;
  align-items: center;
  height: 100%;
}

.header-right {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
}

.auth-btn {
  width: var(--header-control-size);
  height: var(--header-control-size);
  background: transparent;
  border: none;
  line-height: 0;
  padding: 0;
  margin: 0;
  margin-top: 0;
  cursor: pointer;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  box-shadow: none;
  overflow: visible;
}

.auth-btn:hover {
  transform: scale(1.1);
  box-shadow: none;
}

.auth-btn:focus {
  outline: none;
}

.auth-btn:focus-visible {
  outline: 2px solid rgba(var(--vt-c-accent-rgb), 0.7);
  outline-offset: 2px;
}

.auth-icon-svg {
  width: 88%;
  height: 88%;
  display: block;
  color: rgba(var(--vt-c-accent-rgb), 0.96);
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.avatar-btn {
  width: var(--header-control-size);
  height: var(--header-control-size);
  border-radius: 999px;
  background: var(--vt-c-accent);
  color: var(--color-background);
  border: 1px solid var(--vt-c-accent);
  font-weight: 800;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  margin: 0;
  transition: transform 0.2s ease, background 0.2s ease;
  box-shadow: 0 2px 6px rgba(10, 12, 20, 0.45);
}

.avatar-btn:hover {
  transform: scale(1.05);
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

.avatar-btn:focus-visible {
  outline: 2px solid rgba(var(--vt-c-accent-rgb), 0.7);
  outline-offset: 2px;
}

.avatar-initials {
  pointer-events: none;
}

.account-menu {
  min-width: 16rem;
  padding: var(--base-layout-fixed-nav-menu-padding);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.account-menu-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.5rem 0.75rem;
}

.account-menu-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: var(--vt-c-accent);
  color: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.account-menu-identity {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  overflow: hidden;
}

.account-menu-name {
  font-weight: 700;
  color: var(--color-heading);
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.account-menu-email {
  font-size: 0.75rem;
  color: var(--color-text);
  opacity: 0.65;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.account-menu-context {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.5rem 0.65rem;
  background: rgba(var(--vt-c-accent-rgb), 0.08);
  border-radius: 8px;
  margin-bottom: 0.25rem;
}

.account-menu-context-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--vt-c-accent);
  text-align: left;
  margin-bottom: 0.15rem;
}

.account-menu-context-row {
  display: grid;
  grid-template-columns: 4.25rem 1fr;
  column-gap: 0.6rem;
  align-items: baseline;
  font-size: 0.75rem;
  color: var(--color-text);
  text-align: left;
}

.account-menu-context-row strong {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-heading);
  opacity: 0.75;
  text-align: left;
}

.account-menu-context-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  min-width: 0;
}

.account-menu-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.25rem 0;
}

.account-menu-link {
  display: block;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  color: var(--color-text);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: background 0.15s ease, color 0.15s ease;
}

.account-menu-link:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.12);
  color: var(--vt-c-accent);
}

.account-menu .auth-logout-btn {
  margin-top: 0.25rem;
}

.auth-menu {
  position: absolute;
  top: var(--base-layout-fixed-nav-menu-top);
  right: 0;
  background-color: rgba(24, 27, 47, 0.7);
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(24, 27, 47, 0.2));
  padding: var(--base-layout-fixed-nav-menu-padding);
  border-radius: var(--base-layout-fixed-nav-menu-border-radius);
  border: var(--base-layout-fixed-nav-menu-border-width) solid rgba(var(--vt-c-accent-rgb), 0.4);
  box-shadow:
    0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.25),
    0 1.5rem 2.5rem rgba(10, 12, 20, 0.65),
    inset 0 0 40px rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(1.5rem) saturate(1.2);
  min-width: 10rem;
  z-index: var(--base-layout-fixed-nav-menu-z-index);
}

.auth-logout-btn {
  width: 100%;
}

.hamburger {
  border: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: var(--header-control-size);
  height: var(--header-control-size);
  margin: 0;
  margin-top: 0;
  position: relative;
  background: transparent;
  backdrop-filter: none;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
}

.hamburger:hover {
  transform: none;
  box-shadow: none;
}

.hamburger span {
  height: var(--base-layout-fixed-hamburger-line-height);
  width: 72%;
  background: var(--vt-c-accent);
  transition: all 0.25s ease-in-out;
  position: absolute;
  left: 14%;
  transform-origin: center;
}

.hamburger span:nth-child(1) {
  top: 29%;
}

.hamburger span:nth-child(2) {
  top: 50%;
  transform: translateY(-50%);
}

.hamburger span:nth-child(3) {
  bottom: 29%;
}

.hamburger.menu-open span:nth-child(1) {
  transform: rotate(45deg);
  top: 50%;
}

.hamburger.menu-open span:nth-child(2) {
  opacity: 0;
}

.hamburger.menu-open span:nth-child(3) {
  transform: rotate(-45deg);
  bottom: 40%; /* Adjusted for symmetry when closed vs open */
  top: 50%; /* Override bottom when open for centering */
  bottom: auto;
}

.nav-menu {
  position: absolute;
  top: var(--base-layout-fixed-nav-menu-top);
  left: 0;
  right: auto;
  background-color: rgba(24, 27, 47, 0.7);
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(24, 27, 47, 0.2));
  padding: var(--base-layout-fixed-nav-menu-padding);
  border-radius: var(--base-layout-fixed-nav-menu-border-radius);
  z-index: var(--base-layout-fixed-nav-menu-z-index);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-0.3125rem) scale(0.98);
  transform-origin: top left;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: var(--base-layout-fixed-nav-menu-border-width) solid rgba(var(--vt-c-accent-rgb), 0.4);
  box-shadow:
    0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.25),
    0 1.5rem 2.5rem rgba(10, 12, 20, 0.65),
    inset 0 0 40px rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(1.5rem) saturate(1.2);
  min-width: 18rem;
  max-width: min(92vw, 24rem);
}

.nav-menu.menu-open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
  animation: popIn 0.3s cubic-bezier(0.15, 1.8, 0.4, 1);
}

@keyframes popIn {
  0% {
    opacity: 0;
    transform: translateY(-1.25rem) scale(0.95) rotate(-3deg);
  }
  35% {
    transform: translateY(0.25rem) scale(1.02) rotate(2deg);
  }
  60% {
    transform: translateY(-0.125rem) scale(0.98) rotate(-1deg);
  }
  85% {
    transform: translateY(0.0625rem) scale(1.01) rotate(0.5deg);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0);
  }
}

.nav-content {
  min-width: var(--base-layout-fixed-nav-content-min-width);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.user-info {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background: rgba(var(--vt-c-accent-rgb), 0.1);
  border-radius: 8px;
  margin-bottom: 8px;
}

.user-avatar {
  width: 2rem;
  height: 2rem;
  background: var(--vt-c-accent);
  color: var(--color-background);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: var(--text-label);
}

.user-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  flex: 1;
  overflow: hidden;
}

.user-name {
  font-weight: 600;
  color: var(--color-heading);
  font-size: var(--text-label);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: var(--text-caption);
  color: var(--color-text);
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 4px 0;
}

.logout-btn {
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: var(--space-sm);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s;
  font-size: var(--text-label);
  text-align: center;
}

.logout-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
}

.fade-enter-active {
  animation: popIn 0.4s cubic-bezier(0.15, 1.8, 0.4, 1);
}

.fade-leave-active {
  animation: popIn 0.4s cubic-bezier(0.15, 1.8, 0.4, 1) reverse;
}

.nav-menu a {
  color: var(--color-text);
  text-decoration: none;
  display: block;
  padding: var(--base-layout-fixed-nav-link-padding);
  border-radius: var(--base-layout-fixed-nav-link-border-radius);
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  white-space: nowrap;
  font-family: var(--ui-inverse-font-family);
}

.nav-menu a:hover {
  background-color: rgba(var(--vt-c-accent-rgb), 0.1);
  color: var(--vt-c-accent);
  transform: scale(1.05);
}

@media (max-width: 768px) {
  .nav-menu {
    position: fixed;
    top: max(0.5rem, env(safe-area-inset-top));
    left: 0.5rem;
    right: 0.5rem;
    min-height: min(86vh, 44rem);
    max-width: none;
    width: auto;
    padding: 4.5rem 1.15rem 1.25rem;
    background: linear-gradient(165deg, var(--ui-inverse-surface) 0%, var(--ui-inverse-surface-soft) 100%);
    border: 1px solid var(--ui-inverse-border-strong);
    box-shadow: 0 1.25rem 2.75rem rgba(0, 10, 25, 0.36);
    border-radius: 1.5rem;
    backdrop-filter: none;
    transform-origin: top center;
    z-index: calc(var(--base-layout-fixed-nav-menu-z-index) + 20);
  }

  .nav-content {
    min-width: 0;
    width: 100%;
    height: 100%;
    gap: 0.85rem;
  }

  .hamburger.menu-open {
    position: fixed;
    top: max(1rem, calc(env(safe-area-inset-top) + 0.75rem));
    right: 1rem;
    left: auto;
    width: 2.6rem;
    height: 2.6rem;
    z-index: calc(var(--base-layout-fixed-nav-menu-z-index) + 30);
  }

  .hamburger.menu-open span {
    background: var(--ui-inverse-text);
    left: 16%;
    width: 68%;
  }

  .nav-menu a {
    color: var(--ui-inverse-text);
    font-size: clamp(1.6rem, 4.8vw, 2.1rem);
    line-height: 1.12;
    font-weight: 800;
    letter-spacing: 0.01em;
    padding: 0.2rem 0;
    border-radius: 0;
    text-transform: none;
  }

  .nav-menu a:hover {
    background: transparent;
    color: var(--ui-inverse-text);
    transform: none;
  }

  .user-info {
    background: rgba(16, 26, 52, 0.12);
  }

  .user-name,
  .user-email {
    color: var(--ui-inverse-text);
    opacity: 0.8;
  }

  .nav-divider {
    background: rgba(16, 26, 52, 0.16);
  }

  .logout-btn {
    border-color: rgba(158, 20, 55, 0.28);
    color: #8f1434;
    font-family: var(--ui-inverse-font-family);
  }

  .auth-menu {
    background: linear-gradient(165deg, var(--ui-inverse-surface) 0%, var(--ui-inverse-surface-soft) 100%);
    border: 1px solid var(--ui-inverse-border-strong);
    box-shadow: 0 1.25rem 2.75rem rgba(0, 10, 25, 0.36);
    backdrop-filter: none;
  }
}
</style>
